import { useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import { formatDate } from '../dates.ts';
import { usePastAddresses, useRecordPastAddress, useDatePastAddress, type PastAddress } from '../api/pastAddresses.ts';

const field =
  'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 ' +
  'text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/**
 * Where the client used to live (092, Arthur, 2 October). Nothing recorded is
 * lost: a property the agency has discharged lands here when no placement
 * records the client living there, and a person may add one. They are
 * searchable, because an old ID or a hospital record under a former address
 * must find this client, not read as a different person with a similar name.
 *
 * A date is the date they LEFT, where a source records it (a petition noting
 * the day they were found and removed from home, for instance). None is a valid
 * answer; one is never guessed, and it always says where it came from. A date
 * can be added later, once.
 */
export function PastAddresses({ clientId, mayRecord }: { clientId: string; mayRecord: boolean }) {
  const list = usePastAddresses(clientId);
  const [adding, setAdding] = useState(false);
  const rows = list.data?.past_addresses ?? [];

  if (!list.data && !mayRecord) return null;

  return (
    <div className="mt-4 border-t border-neutral-200 pt-3">
      <h3 className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">Past addresses</h3>
      {list.error && !list.data && <p className="mt-1 text-sm text-rose-700">Past addresses could not be loaded.</p>}
      {list.data && rows.length === 0 && <p className="mt-1 text-sm text-neutral-500">None recorded.</p>}
      {rows.length > 0 && (
        <ul className="mt-1 space-y-2">
          {rows.map((a) => <Row key={a.id} a={a} clientId={clientId} mayRecord={mayRecord} />)}
        </ul>
      )}
      {mayRecord && !adding && (
        <button type="button" className={`${secondary} mt-2`} onClick={() => setAdding(true)}>
          Add a past address
        </button>
      )}
      {adding && <AddForm clientId={clientId} onDone={() => setAdding(false)} />}
    </div>
  );
}

function addressLine(a: PastAddress): string {
  const street = [a.street_address, a.unit].filter(Boolean).join(', ');
  const place = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, place].filter(Boolean).join(', ');
}

function Row({ a, clientId, mayRecord }: { a: PastAddress; clientId: string; mayRecord: boolean }) {
  const [dating, setDating] = useState(false);
  return (
    <li className="text-[0.9375rem] text-neutral-800">
      <span className="font-medium">{addressLine(a)}</span>
      <span className="block text-sm text-neutral-600">
        {a.moved_out_on
          ? <>Left {formatDate(a.moved_out_on)} · {a.moved_out_source}</>
          : 'When they left is not recorded'}
        {a.source === 'Property discharged' && (
          <> · from the property record{a.property_disposition ? ` (${a.property_disposition})` : ''}</>
        )}
      </span>
      {mayRecord && !a.moved_out_on && !dating && (
        <button type="button" className={`${secondary} mt-1`} onClick={() => setDating(true)}>
          Add the date they left
        </button>
      )}
      {dating && <DateForm id={a.id} clientId={clientId} onDone={() => setDating(false)} />}
    </li>
  );
}

function DateForm({ id, clientId, onDone }: { id: string; clientId: string; onDone: () => void }) {
  const date = useDatePastAddress(clientId);
  const [on, setOn] = useState('');
  const [source, setSource] = useState('');
  const sending = useRef(false);
  const ready = /^\d{4}-\d{2}-\d{2}$/.test(on) && source.trim().length >= 3;
  return (
    <form
      className="mt-2 space-y-2 rounded-lg bg-neutral-50 p-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!ready || sending.current) return;
        sending.current = true;
        date.mutate({ id, moved_out_on: on, moved_out_source: source.trim() }, {
          onSuccess: onDone,
          onSettled: () => { sending.current = false; },
        });
      }}
    >
      <label className={label} htmlFor={`left-${id}`}>The date they left</label>
      <input id={`left-${id}`} type="date" className={field} value={on} onChange={(e) => setOn(e.target.value)} />
      <label className={label} htmlFor={`src-${id}`}>Where the date comes from</label>
      <input id={`src-${id}`} className={field} value={source} maxLength={200}
             placeholder="e.g. Petition: found and removed from home by EMS"
             onChange={(e) => setSource(e.target.value)} />
      <Failure error={date.error} />
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!ready || date.isPending}>Record the date</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function AddForm({ clientId, onDone }: { clientId: string; onDone: () => void }) {
  const record = useRecordPastAddress(clientId);
  const [d, setD] = useState({ street_address: '', unit: '', city: '', state: '', zip: '', moved_out_on: '', moved_out_source: '' });
  const sending = useRef(false);
  const set = (k: keyof typeof d) => (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: e.target.value });
  const dateWithoutSource = d.moved_out_on !== '' && d.moved_out_source.trim().length < 3;
  const ready = d.street_address.trim() !== '' && !dateWithoutSource;
  return (
    <form
      className="mt-2 space-y-2 rounded-lg bg-neutral-50 p-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!ready || sending.current) return;
        sending.current = true;
        const t = (v: string) => (v.trim() === '' ? undefined : v.trim());
        record.mutate({
          street_address: d.street_address.trim(), unit: t(d.unit), city: t(d.city), state: t(d.state), zip: t(d.zip),
          moved_out_on: t(d.moved_out_on), moved_out_source: t(d.moved_out_source),
        }, {
          onSuccess: onDone,
          onSettled: () => { sending.current = false; },
        });
      }}
    >
      <label className={label} htmlFor="pa-street">Street</label>
      <input id="pa-street" className={field} value={d.street_address} maxLength={160} onChange={set('street_address')} />
      <div className="grid gap-2 sm:grid-cols-4">
        <div><label className={label} htmlFor="pa-unit">Unit</label>
          <input id="pa-unit" className={field} value={d.unit} maxLength={32} onChange={set('unit')} /></div>
        <div><label className={label} htmlFor="pa-city">Town</label>
          <input id="pa-city" className={field} value={d.city} maxLength={96} onChange={set('city')} /></div>
        <div><label className={label} htmlFor="pa-state">State</label>
          <input id="pa-state" className={field} value={d.state} maxLength={32} onChange={set('state')} /></div>
        <div><label className={label} htmlFor="pa-zip">Zip</label>
          <input id="pa-zip" className={field} value={d.zip} maxLength={12} onChange={set('zip')} /></div>
      </div>
      <label className={label} htmlFor="pa-left">The date they left, if a source records it</label>
      <input id="pa-left" type="date" className={field} value={d.moved_out_on} onChange={set('moved_out_on')} />
      {d.moved_out_on !== '' && (
        <>
          <label className={label} htmlFor="pa-src">Where the date comes from</label>
          <input id="pa-src" className={field} value={d.moved_out_source} maxLength={200}
                 placeholder="e.g. Petition: found and removed from home by EMS" onChange={set('moved_out_source')} />
        </>
      )}
      <Failure error={record.error} />
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!ready || record.isPending}>Record the past address</button>
        <button type="button" className={secondary} onClick={onDone}>Cancel</button>
      </div>
    </form>
  );
}

function Failure({ error }: { error: Error | null }) {
  if (!error) return null;
  const api = error instanceof ApiError ? error : null;
  return (
    <p className="text-sm text-rose-700">
      {error.message}
      {api?.hint && <span className="block">{api.hint}</span>}
    </p>
  );
}
